import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Tooltip,
  Grid,
  GridItem,
  Portal,
} from '@chakra-ui/react'
import { FiSearch, FiUser, FiLogOut, FiFilter, FiShield, FiHelpCircle, FiMenu } from 'react-icons/fi'
import { useAuth } from '@/contexts/AuthContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import LanguageSwitcher from '@/components/Common/LanguageSwitcher'

interface NavigationBarProps {
  onSidebarToggle?: () => void
  isSidebarOpen?: boolean
}

export default function NavigationBar({ onSidebarToggle, isSidebarOpen = true }: NavigationBarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { t } = useAppTranslation(['common', 'search', 'auth'])
  const [searchQuery, setSearchQuery] = useState('')

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) return

    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
  }

  return (
    <Box
      className="electron-navbar"
      bg="white"
      // borderBottom="1px solid"
      // borderColor="gray.200"
      px={{ base: 2, md: 6 }}
      h="64px"
      position="sticky"
      top={0}
      zIndex={1000}
      // boxShadow="sm"
    >
      <Grid templateColumns="1fr auto" h="full" alignItems="center" gap={6}>
        {/* Left Column - Sidebar toggle + Search */}
        <GridItem display="flex" alignItems="center" gap={4}>
          {onSidebarToggle && (
            <Tooltip
              label={t(isSidebarOpen ? 'buttons.hideSidebar' : 'buttons.showSidebar', {
                ns: 'common',
              })}
              placement="bottom"
            >
              <IconButton
                aria-label={t(isSidebarOpen ? 'buttons.hideSidebar' : 'buttons.showSidebar', {
                  ns: 'common',
                })}
                icon={<FiMenu />}
                variant="ghost"
                color="gray.600"
                _hover={{ bg: 'gray.100', color: 'gray.800' }}
                borderRadius="lg"
                flexShrink={0}
                onClick={onSidebarToggle}
              />
            </Tooltip>
          )}
          <Box as="form" onSubmit={handleSearch} w="full" maxW="500px">
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="12">
                <FiSearch color="gray.400" size="18px" />
              </InputLeftElement>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('placeholder', { ns: 'search' })}
                name="search"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                height="12"
                boxShadow="sm"
                fontSize="sm"
                pr="12"
                _focus={{
                  borderColor: 'brand.500',
                  boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                  bg: 'white',
                }}
                _placeholder={{
                  color: 'gray.500',
                  fontSize: 'sm',
                }}
                transition="all 0.2s"
              />
              <InputRightElement h="12" w="12">
                <Tooltip label={t('advancedSearch.title', { ns: 'search' })} placement="bottom">
                  <IconButton
                    aria-label={t('advancedSearch.title', { ns: 'search' })}
                    icon={<FiFilter />}
                    size="sm"
                    variant="ghost"
                    color="gray.500"
                    _hover={{
                      color: 'brand.500',
                      bg: 'brand.50',
                    }}
                    onClick={() =>
                      navigate(
                        `/search${searchQuery.trim() ? `?q=${encodeURIComponent(searchQuery.trim())}` : ''}`
                      )
                    }
                  />
                </Tooltip>
              </InputRightElement>
            </InputGroup>
          </Box>
        </GridItem>

        {/* Right Column - Enhanced User Profile */}
        <GridItem>
          <HStack spacing={2}>
            {/* Language Switcher */}
            <LanguageSwitcher variant="compact" size="sm" />

            {/* User Profile Menu */}
            <Menu>
              <MenuButton>
                <Box
                  cursor="pointer"
                  py={1}
                  px={2}
                  borderRadius="lg"
                  _hover={{ bg: 'gray.50' }}
                  transition="colors 0.15s"
                >
                  <Avatar
                    size="sm"
                    name={user?.name}
                    ring={2}
                    ringColor="gray.200"
                    boxShadow="sm"
                  />
                </Box>
              </MenuButton>

              <Portal>
              <MenuList
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="xl"
                py={2}
                zIndex={1500}
              >
                <MenuItem
                  icon={<FiUser />}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate('/account')}
                >
                  {t('navigation.profile', { ns: 'common' })}
                </MenuItem>
                <MenuItem
                  icon={<FiShield />}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate('/account/security')}
                >
                  {t('navigation.security', { ns: 'common' })}
                </MenuItem>
                <MenuItem
                  icon={<FiHelpCircle />}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => navigate('/help')}
                >
                  {t('navigation.help', { ns: 'common' })}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={<FiLogOut />}
                  onClick={handleLogout}
                  _hover={{ bg: 'red.50', color: 'red.600' }}
                  color="gray.700"
                >
                  {t('logout.button', { ns: 'auth' })}
                </MenuItem>
              </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </GridItem>
      </Grid>

    </Box>
  )
}
